import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, finalize, map, shareReplay, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Role } from '../../../utils/enums/role.enum';
import {
  ChangePasswordRequest,
  LoginRequest,
  RefreshResponse,
  SsoCallbackResponse,
  UserApi,
} from '../sso/sso.interface';
import { PATH_LOGIN } from '../../../routes/routes.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  private readonly endpoint = 'auth';
  private readonly USER_KEY = 'api_user';
  private readonly TOKEN_KEY = 'api_token';
  private readonly EXPIRES_KEY = 'api_token_expires';
  private readonly apiUrl = environment.apiUrl;

  private currentUser = signal<UserApi | null>(null);
  private currentToken = signal<string | null>(null);
  private isAuthenticated = signal<boolean>(false);

  /** Refresh single-flight: N requests en 401 disparan un solo POST /auth/refresh */
  private refreshRequest: Observable<string> | null = null;

  user = this.currentUser.asReadonly();
  loggedIn = this.isAuthenticated.asReadonly();

  constructor() {
    // Restaurar sesión del localStorage (para que F5 no cierre la sesión)
    const stored = localStorage.getItem(this.USER_KEY);
    if (stored) {
      try {
        const user: UserApi = JSON.parse(stored);
        // Sesiones guardadas antes del modelo de roles/empresas: se pide ingresar de nuevo.
        if (user.role == null) throw new Error('Sesión sin rol');
        this.currentUser.set(user);
        this.currentToken.set(localStorage.getItem(this.TOKEN_KEY));
        this.isAuthenticated.set(true);
      } catch {
        this.clearLocalSession();
      }
    }
  }

  /**
   * Login propio de minino. El BE deja el refresh token en una cookie httpOnly
   * (`withCredentials`) y devuelve un accessToken corto.
   */
  login(request: LoginRequest): Observable<SsoCallbackResponse> {
    return this.http
      .post<any>(`${this.apiUrl}${this.endpoint}/login`, request, {
        withCredentials: true,
      })
      .pipe(map((response) => this.handleSessionResponse(response)));
  }

  /**
   * Cambia la contraseña. El BE cierra las demás sesiones y devuelve una sesión nueva
   * (sin la marca de contraseña temporal), que reemplaza a la actual.
   */
  changePassword(request: ChangePasswordRequest): Observable<SsoCallbackResponse> {
    return this.http
      .post<any>(`${this.apiUrl}${this.endpoint}/change-password`, request, {
        withCredentials: true,
      })
      .pipe(map((response) => this.handleSessionResponse(response)));
  }

  mustChangePassword(): boolean {
    return !!this.currentUser()?.mustChangePassword;
  }

  /** [SSO deshabilitado] Canje del ssoToken de Fidel contra el BE propio (el BE llama a /verifytoken). */
  ssoCallback(ssoToken: string): Observable<SsoCallbackResponse> {
    return this.http
      .post<any>(
        `${this.apiUrl}${this.endpoint}/sso/callback`,
        { ssoToken },
        { withCredentials: true },
      )
      .pipe(
        map((response) => ({
          accessToken: response.accessToken,
          expiresIn: response.expiresIn,
          user: this.mapUser(response.user),
        })),
        tap(({ accessToken, expiresIn, user }) => {
          if (accessToken) this.storeSession(user, accessToken, expiresIn);
        }),
      );
  }

  /** Renovación con refresh token (cookie httpOnly). Comparte una sola request en vuelo. */
  refreshAccessToken(): Observable<string> {
    if (!this.refreshRequest) {
      this.refreshRequest = this.http
        .post<RefreshResponse>(
          `${this.apiUrl}${this.endpoint}/refresh`,
          {},
          { withCredentials: true },
        )
        .pipe(
          map(({ accessToken, expiresIn }) => {
            this.storeToken(accessToken, expiresIn);
            return accessToken;
          }),
          finalize(() => (this.refreshRequest = null)),
          shareReplay(1),
        );
    }
    return this.refreshRequest;
  }

  /** Hidrata el usuario desde el BE sin confiar en el localStorage. */
  me(): Observable<UserApi> {
    return this.http
      .get<any>(`${this.apiUrl}${this.endpoint}/me`)
      .pipe(map((response) => this.mapUser(response)));
  }

  /** Invalida el refresh token del lado del servidor (y devuelve la URL de logout del SSO, si está habilitado). */
  logoutServer(): Observable<{ ssoLogoutUrl: string; ssoLoginUrl: string }> {
    return this.http.post<{ ssoLogoutUrl: string; ssoLoginUrl: string }>(
      `${this.apiUrl}${this.endpoint}/logout`,
      {},
      { withCredentials: true },
    );
  }

  /** Limpia la sesión local (signals + localStorage) sin navegar */
  clearLocalSession(): void {
    this.currentUser.set(null);
    this.currentToken.set(null);
    this.isAuthenticated.set(false);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.EXPIRES_KEY);
  }

  logout(): void {
    this.clearLocalSession();
    this.router.navigate([PATH_LOGIN]);
  }

  getUserRoleId(): number {
    return this.currentUser()?.rolId || 0;
  }

  getToken(): string | null {
    return this.currentToken();
  }

  isTokenExpired(): boolean {
    if (!this.currentToken()) return true;
    const expiresAt = localStorage.getItem(this.EXPIRES_KEY);
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() <= Date.now();
  }

  hasAnyRole(roleIds: number[]): boolean {
    const userRole = this.getUserRoleId();
    return roleIds.includes(userRole);
  }

  isAdmin(): boolean {
    return this.hasAnyRole([Role.Admin]);
  }

  /** Cuentas: el SuperAdmin (todas) y el Admin (las de su empresa). La API lo exige igual. */
  canManageUsers(): boolean {
    const role = this.currentUser()?.role;
    return role === Role.Admin || role === Role.SuperAdmin;
  }

  /** Equipo de minino: empresas, cuentas y plantillas. No opera dentro de las empresas. */
  isSuperAdmin(): boolean {
    return this.currentUser()?.role === Role.SuperAdmin;
  }

  /** Editor de videos: el SuperAdmin (plantillas) y el Admin (ediciones de su empresa). */
  canEditVideos(): boolean {
    return this.isCompanyAdmin();
  }

  /** Admin de la empresa: además de todo, gestiona sucursales, categorías y cuentas. */
  isCompanyAdmin(): boolean {
    return this.currentUser()?.role === Role.Admin;
  }

  private handleSessionResponse(response: any): SsoCallbackResponse {
    const session: SsoCallbackResponse = {
      accessToken: response.accessToken,
      expiresIn: response.expiresIn,
      user: this.mapUser(response.user),
    };
    if (session.accessToken)
      this.storeSession(session.user, session.accessToken, session.expiresIn);
    return session;
  }

  private mapUser(rawUser: any): UserApi {
    return {
      ssoUserId: rawUser?.ssoUserId,
      userId: rawUser?.userId ?? rawUser?.id,
      email: rawUser?.email ?? rawUser?.userName,
      fullName: rawUser?.fullName ?? rawUser?.displayName,
      // Admin y Usuario ven los módulos con la vista completa (las pantallas comparan
      // `rolId === Role.Admin`); qué datos ve cada uno lo filtra la API. El rol real va en `role`.
      rolId: Role.Admin,
      superAdmin: rawUser?.rolId === Role.SuperAdmin,
      role: [Role.SuperAdmin, Role.Admin].includes(rawUser?.rolId) ? rawUser.rolId : Role.Usuario,
      empresaId: rawUser?.empresaId ?? null,
      empresaName: rawUser?.empresaName ?? null,
      type: rawUser?.type,
      permissions: rawUser?.permissions,
      franchiseIds: rawUser?.franchiseIds
        ? rawUser.franchiseIds.map((id: any) => id.toString())
        : [],
      mustChangePassword: !!rawUser?.mustChangePassword,
    };
  }

  private storeSession(user: UserApi, token: string, expiresIn: number): void {
    this.currentUser.set(user);
    this.isAuthenticated.set(true);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this.storeToken(token, expiresIn);
  }

  private storeToken(token: string, expiresIn: number): void {
    this.currentToken.set(token);
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(
      this.EXPIRES_KEY,
      new Date(Date.now() + (expiresIn ?? 0) * 1000).toISOString(),
    );
  }
}
